export function toMillis(ts: number): number {
    return ts > 1e12 ? ts : ts * 1000;
}

export function formatTime(value: unknown): string {
    if (typeof value !== 'number') return '';
    const d = new Date(toMillis(value));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
}

/**
 * Epoch seconds or milliseconds from APIs; numeric strings coerced.
 * Use for event/file timestamps where responses may be either unit.
 */
export function formatApiDateTime(value: unknown, empty: string = '—'): string {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n) || n <= 0) return empty;
    const d = new Date(toMillis(n));
    if (Number.isNaN(d.getTime())) return empty;
    try {
        return d.toLocaleString();
    } catch {
        return empty;
    }
}

export function formatTs(ts?: number): string {
    if (!ts || ts <= 0) return '-';
    try {
        return new Date(toMillis(ts)).toLocaleString();
    } catch {
        return String(ts);
    }
}

function shortIdentity(value: string): string {
    if (value.length <= 18) return value;
    return `${value.slice(0, 8)}...${value.slice(-6)}`;
}

export function formatUserIdentityDisplay(
    value: unknown,
    userIdToDisplayName: Record<string, string> = {},
): string {
    const raw = String(value ?? '').trim();
    if (!raw) return '-';
    const mapped = String(userIdToDisplayName[raw] ?? '').trim();
    if (mapped) return mapped;
    if (raw.includes('@')) return raw;
    return shortIdentity(raw);
}

/** Normalize any status string to trimmed uppercase (e.g. "in_progress" → "IN_PROGRESS"). */
export function normStatus(s?: string): string {
    return (s || '').trim().toUpperCase();
}

/** Format a millisecond duration as HH:MM:SS or MM:SS. */
export function formatElapsed(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (x: number) => String(x).padStart(2, '0');
    if (hh > 0) return `${hh}:${pad(mm)}:${pad(ss)}`;
    return `${mm}:${pad(ss)}`;
}

/** Join class name parts, filtering falsy values. */
export function cls(...parts: Array<string | false | null | undefined>): string {
    return parts.filter(Boolean).join(' ');
}

/** Return trimmed string if non-empty, otherwise the fallback. */
export function nonEmptyOr<T extends string | undefined>(v: T, fallback: string): string {
    if (typeof v === 'string' && v.trim().length > 0) return v;
    return fallback;
}